import type { BondOrder } from './editor';
import { mapPathControls, movePathControl, movePathNode, pathSegment } from './pathEditing';
import type { DrawingLine, NormalizedPoint } from '../model';
import type { DrawingAtom } from './graphDrawing';
import type { PaperSize } from './geometry';
import type { RenderableAnnotation } from './annotationRendering';
import { normalizedToPixel } from './bonds';

export type PaperShape = Extract<RenderableAnnotation, { kind: 'shape' }>;
export interface EditingBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}
export interface ObjectHandle {
  id: string;
  point: NormalizedPoint;
  anchor?: NormalizedPoint;
  selected?: boolean;
}
export const samePosition = (a: NormalizedPoint, b: NormalizedPoint) => Math.hypot(a.x - b.x, a.y - b.y) < 1e-6;
export function boundsOf(points: readonly NormalizedPoint[]) {
  const minX = Math.min(...points.map((p) => p.x)),
    maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y)),
    maxY = Math.max(...points.map((p) => p.y));
  return { minX, minY, maxX, maxY, center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 } };
}
export function rotatePoint(
  point: NormalizedPoint,
  center: NormalizedPoint,
  radians: number,
  size: PaperSize
): NormalizedPoint {
  const x = (point.x - center.x) * size.width,
    y = (point.y - center.y) * size.height;
  return {
    x: center.x + (x * Math.cos(radians) - y * Math.sin(radians)) / size.width,
    y: center.y + (x * Math.sin(radians) + y * Math.cos(radians)) / size.height
  };
}
export function objectHandles(
  points: readonly NormalizedPoint[],
  size: PaperSize,
  zoom: number,
  rotation = 0
): ObjectHandle[] {
  if (!points.length) return [];
  const b = boundsOf(points);
  return [
    { id: 'move', point: b.center },
    { id: 'scale', point: { x: b.maxX + 28 / zoom / size.width, y: b.maxY + 28 / zoom / size.height } },
    { id: 'rotate', point: { x: b.center.x, y: b.minY - 28 / zoom / size.height } }
  ].map((h) => ({ ...h, point: rotatePoint(h.point, b.center, (rotation * Math.PI) / 180, size) }));
}
export function shapeHandles(shape: PaperShape, size: PaperSize, zoom: number): ObjectHandle[] {
  const b = boundsOf(shape.points);
  return [
    ...(shape.callout
      ? [
          { id: 'move', point: b.center },
          { id: 'scale', point: { x: b.maxX + 28 / zoom / size.width, y: b.maxY + 28 / zoom / size.height } },
          { id: 'pointer-tip', point: shape.callout.tip }
        ]
      : objectHandles(shape.points, size, zoom, shape.rotation)),
    ...(shape.shape === 'path'
      ? []
      : [
          {
            id: 'resize',
            point: rotatePoint({ x: b.minX, y: b.minY }, b.center, ((shape.rotation ?? 0) * Math.PI) / 180, size)
          }
        ])
  ];
}
export function transformPoints(
  points: readonly NormalizedPoint[],
  start: NormalizedPoint,
  end: NormalizedPoint,
  mode: string,
  size: PaperSize,
  shift = false,
  limits: EditingBounds = { minX: 0, minY: 0, maxX: 1, maxY: 1 },
  basisPoints: readonly NormalizedPoint[] = points
): readonly NormalizedPoint[] {
  const b = boundsOf(basisPoints);
  if (mode === 'move') {
    const dx = Math.max(limits.minX - b.minX, Math.min(limits.maxX - b.maxX, end.x - start.x));
    const dy = Math.max(limits.minY - b.minY, Math.min(limits.maxY - b.maxY, end.y - start.y));
    return points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
  }
  const a = normalizedToPixel(start, size),
    z = normalizedToPixel(end, size),
    c = normalizedToPixel(b.center, size);
  let next: readonly NormalizedPoint[];
  if (mode === 'rotate') {
    let angle = Math.atan2(z.y - c.y, z.x - c.x) - Math.atan2(a.y - c.y, a.x - c.x);
    if (shift) angle = (Math.round(angle / (Math.PI / 12)) * Math.PI) / 12;
    next = points.map((p) => rotatePoint(p, b.center, angle, size));
  } else {
    const radius = Math.hypot((b.maxX - b.center.x) * size.width, (b.maxY - b.center.y) * size.height);
    const scale = Math.max(
      0.15,
      1 + (Math.hypot(z.x - c.x, z.y - c.y) - Math.hypot(a.x - c.x, a.y - c.y)) / Math.max(1, radius)
    );
    next = points.map((p) => ({
      x: b.center.x + (p.x - b.center.x) * scale,
      y: b.center.y + (p.y - b.center.y) * scale
    }));
  }
  return next.every((p) => p.x >= limits.minX && p.x <= limits.maxX && p.y >= limits.minY && p.y <= limits.maxY)
    ? next
    : points;
}
export function editShape(
  shape: PaperShape,
  start: NormalizedPoint,
  end: NormalizedPoint,
  mode: string,
  size: PaperSize,
  shift: boolean
): PaperShape {
  const b = boundsOf(shape.points);
  if (mode.startsWith('point-')) return movePathNode(shape, Number(mode.slice(6)), end);
  const control = /^control-(incoming|outgoing)-(\d+)$/.exec(mode);
  if (control) return movePathControl(shape, Number(control[2]), control[1] as 'incoming' | 'outgoing', end);
  if (shape.shape === 'path') {
    const all = [
      ...shape.points,
      ...(shape.pathControls ?? []).flatMap((c) => [c.incoming, c.outgoing].filter((p) => p !== undefined))
    ];
    const next = transformPoints(
      all,
      start,
      end,
      mode,
      size,
      shift,
      {
        minX: -Infinity,
        minY: -Infinity,
        maxX: Infinity,
        maxY: Infinity
      },
      shape.points
    );
    let i = shape.points.length;
    return {
      ...shape,
      points: next.slice(0, i),
      ...(shape.pathControls ? { pathControls: mapPathControls(shape.pathControls, () => next[i++]!) } : {})
    };
  }
  if (mode === 'rotate') {
    const a = normalizedToPixel(start, size),
      z = normalizedToPixel(end, size),
      c = normalizedToPixel(b.center, size);
    let rotation =
      (shape.rotation ?? 0) + ((Math.atan2(z.y - c.y, z.x - c.x) - Math.atan2(a.y - c.y, a.x - c.x)) * 180) / Math.PI;
    if (shift) rotation = Math.round(rotation / 15) * 15;
    return { ...shape, rotation };
  }
  if (mode === 'resize') {
    const a = rotatePoint(start, b.center, (-(shape.rotation ?? 0) * Math.PI) / 180, size);
    const z = rotatePoint(end, b.center, (-(shape.rotation ?? 0) * Math.PI) / 180, size);
    let width = Math.max(4 / size.width, b.maxX - b.minX - 2 * (z.x - a.x));
    let height = Math.max(4 / size.height, b.maxY - b.minY - 2 * (z.y - a.y));
    if (shift) {
      const scale = Math.max(width / (b.maxX - b.minX), height / (b.maxY - b.minY));
      width = (b.maxX - b.minX) * scale;
      height = (b.maxY - b.minY) * scale;
    }
    return {
      ...shape,
      points: [
        { x: b.center.x - width / 2, y: b.center.y - height / 2 },
        { x: b.center.x + width / 2, y: b.center.y + height / 2 }
      ]
    };
  }
  return {
    ...shape,
    ...(shape.callout && mode === 'move' && !shape.callout.attachment
      ? {
          callout: {
            ...shape.callout,
            tip: { x: shape.callout.tip.x + end.x - start.x, y: shape.callout.tip.y + end.y - start.y }
          }
        }
      : {}),
    points: transformPoints(shape.points, start, end, mode, size, shift, {
      minX: -Infinity,
      minY: -Infinity,
      maxX: Infinity,
      maxY: Infinity
    })
  };
}
export function atomIdsForLines(atoms: readonly DrawingAtom[], lines: readonly DrawingLine[]): string[] {
  return atoms
    .filter((a) => lines.some((l) => samePosition(a.position, l.start) || samePosition(a.position, l.end)))
    .map((a) => a.id);
}
export type BondAtomIds = Readonly<Record<string, readonly [string, string]>>;
export function findBondAtomIds(atoms: readonly DrawingAtom[], lines: readonly DrawingLine[]): BondAtomIds {
  const key = (p: NormalizedPoint) => `${p.x},${p.y}`;
  const byPosition = new Map(atoms.map((a) => [key(a.position), a.id]));
  return Object.fromEntries(
    lines.flatMap((l) => {
      const a = byPosition.get(key(l.start)) ?? atoms.find((a) => samePosition(a.position, l.start))?.id;
      const b = byPosition.get(key(l.end)) ?? atoms.find((a) => samePosition(a.position, l.end))?.id;
      return a && b ? [[l.id, [a, b] as const]] : [];
    })
  );
}
export interface CanvasRing {
  atomIds: string[];
  bondIds: string[];
}
// A shortest alternative path for each edge finds the small selectable rings in fused/spiro
// systems without enumerating every possible cycle. Stable IDs also work for imported graphs.
export function canvasRings(
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  endpoints: BondAtomIds = findBondAtomIds(atoms, lines)
): CanvasRing[] {
  const edges = lines.flatMap((l) => {
    const pair = endpoints[l.id];
    return pair ? [{ id: l.id, a: pair[0], b: pair[1] }] : [];
  });
  const adjacency = new Map<string, { id: string; other: string }[]>();
  for (const e of edges)
    for (const [id, other] of [
      [e.a, e.b],
      [e.b, e.a]
    ])
      adjacency.set(id!, [...(adjacency.get(id!) ?? []), { id: e.id, other: other! }]);
  const rings = new Map<string, CanvasRing>();
  for (const edge of edges) {
    const queue = [{ at: edge.a, atomIds: [edge.a], bondIds: [] as string[] }];
    const seen = new Set([edge.a]);
    for (let i = 0; i < queue.length; i++) {
      const path = queue[i]!;
      if (path.at === edge.b) {
        const bondIds = [...path.bondIds, edge.id];
        rings.set([...bondIds].sort().join('|'), { atomIds: path.atomIds, bondIds });
        break;
      }
      for (const next of adjacency.get(path.at) ?? [])
        if (next.id !== edge.id && !seen.has(next.other)) {
          seen.add(next.other);
          queue.push({ at: next.other, atomIds: [...path.atomIds, next.other], bondIds: [...path.bondIds, next.id] });
        }
    }
  }
  return [...rings.values()].sort((a, b) => a.atomIds.length - b.atomIds.length);
}
// #422: a double bond that belongs to exactly one ring should render its second line toward that
// ring's interior. A bond shared between two rings (fusion) is excluded here -- which side it
// should face is ambiguous and left to a future per-bond user choice, not decided automatically.
export function nonFusedRingDoubleBondCenters(
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  bondOrders: Readonly<Record<string, BondOrder>>,
  endpoints: BondAtomIds = findBondAtomIds(atoms, lines)
): Readonly<Record<string, NormalizedPoint>> {
  const rings = canvasRings(atoms, lines, endpoints);
  const membership = new Map<string, number>();
  for (const ring of rings) for (const id of ring.bondIds) membership.set(id, (membership.get(id) ?? 0) + 1);
  const byAtomId = new Map(atoms.map((a) => [a.id, a.position]));
  const centers: Record<string, NormalizedPoint> = {};
  for (const ring of rings) {
    const center = {
      x: ring.atomIds.reduce((sum, id) => sum + byAtomId.get(id)!.x, 0) / ring.atomIds.length,
      y: ring.atomIds.reduce((sum, id) => sum + byAtomId.get(id)!.y, 0) / ring.atomIds.length
    };
    for (const id of ring.bondIds) if (membership.get(id) === 1 && (bondOrders[id] ?? 1) === 2) centers[id] = center;
  }
  return centers;
}
export interface FusionBondSide {
  readonly facingAtomId: string;
  readonly center: NormalizedPoint;
}
// #453: for a double bond shared by exactly two rings, the atom (private to each ring -- present
// in it but not the other) that identifies "facing this ring", plus that ring's centroid for
// rendering. `canvasRings` already sorts rings smallest-first, so sides[0] is a stable, sensible
// default when the user hasn't chosen explicitly.
export function fusedRingDoubleBondSides(
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  bondOrders: Readonly<Record<string, BondOrder>>,
  endpoints: BondAtomIds = findBondAtomIds(atoms, lines)
): Readonly<Record<string, readonly [FusionBondSide, FusionBondSide]>> {
  const rings = canvasRings(atoms, lines, endpoints);
  const boundingRings = new Map<string, CanvasRing[]>();
  for (const ring of rings)
    for (const id of ring.bondIds) boundingRings.set(id, [...(boundingRings.get(id) ?? []), ring]);
  const byAtomId = new Map(atoms.map((a) => [a.id, a.position]));
  const centerOf = (ring: CanvasRing) => ({
    x: ring.atomIds.reduce((sum, id) => sum + byAtomId.get(id)!.x, 0) / ring.atomIds.length,
    y: ring.atomIds.reduce((sum, id) => sum + byAtomId.get(id)!.y, 0) / ring.atomIds.length
  });
  const result: Record<string, readonly [FusionBondSide, FusionBondSide]> = {};
  for (const [bondId, [ringA, ringB, ...rest]] of boundingRings) {
    if (!ringB || rest.length > 0 || (bondOrders[bondId] ?? 1) !== 2) continue;
    const facingA = ringA!.atomIds.find((id) => !ringB.atomIds.includes(id));
    const facingB = ringB.atomIds.find((id) => !ringA!.atomIds.includes(id));
    if (!facingA || !facingB) continue;
    result[bondId] = [
      { facingAtomId: facingA, center: centerOf(ringA!) },
      { facingAtomId: facingB, center: centerOf(ringB) }
    ];
  }
  return result;
}
export function insidePolygon(p: NormalizedPoint, points: readonly NormalizedPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!,
      b = points[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Conservative visible extent, including rotated corners, for export clipping. */
export function shapeExtentPoints(shape: PaperShape, size: PaperSize): readonly NormalizedPoint[] {
  if (shape.callout) return [...shape.points, shape.callout.tip];
  if (shape.shape === 'path')
    return [
      ...shape.points,
      ...(shape.pathControls ?? []).flatMap((c) => [c.incoming, c.outgoing].filter((p) => p !== undefined))
    ];
  if (!shape.rotation) return shape.points;
  const b = boundsOf(shape.points);
  return [
    { x: b.minX, y: b.minY },
    { x: b.maxX, y: b.minY },
    { x: b.maxX, y: b.maxY },
    { x: b.minX, y: b.maxY }
  ].map((p) => rotatePoint(p, b.center, (shape.rotation! * Math.PI) / 180, size));
}

export function pathNodeHandles(shape: PaperShape, selected: readonly number[]): ObjectHandle[] {
  const handles: ObjectHandle[] = shape.points.map((point, i) => ({
    id: `point-${i}`,
    point,
    selected: selected.includes(i)
  }));
  for (const i of selected) {
    if (!shape.points[i]) continue;
    if (i > 0 || shape.closed)
      handles.push({
        id: `control-incoming-${i}`,
        point: pathSegment(shape, (i - 1 + shape.points.length) % shape.points.length)[2],
        anchor: shape.points[i]
      });
    if (i < shape.points.length - 1 || shape.closed)
      handles.push({ id: `control-outgoing-${i}`, point: pathSegment(shape, i)[1], anchor: shape.points[i] });
  }
  return handles;
}
